import { css } from "@emotion/react";
import { useRouter } from "next/router";
import * as React from "react";
import { useEffect, useState } from "react";

import {
  useClock,
  useTimeSinceNavigation,
} from "@zapier/service/src/performance";
import { addTruthyParamsToUrl } from "@zapier/url-utils";

import Reloader from "@zapier/reporting/src/components/global/Reloader";
import {
  useAccountAndUser,
  useDeepEqualEffect,
  useHasHydrated,
  useIFrameResize,
  useSize,
} from "@zapier/reporting/src/hooks";
import { ZapRun } from "@zapier/reporting/src/hooks/useRuns";
import {
  FilterChangeType,
  HistoryFiltersTypes,
  SelectRunOptions,
} from "@zapier/reporting/src/types";
import {
  areFiltersEmpty,
  getActiveFilterNames,
  getResponsiveStyles,
} from "@zapier/reporting/src/utils";
import { NoRunsSurface } from "../NoRunsSurface";
import AutoReplaySwitch from "./AutoReplaySwitch";
import { EmbedDateFilter } from "./EmbedDateFilter";
import ListSelectionDropdown from "./ListSelectionDropdown";
import RunActions from "./RunActions";
import RunList from "./RunList";
import StatusFilter from "./StatusFilter";

type Props = {
  actionableRunCount: any;
  filters: Partial<HistoryFiltersTypes>;
  getSelectedRuns: (selectedIds: string[]) => any[];
  handleFilterChange: FilterChangeType;
  handleFilterReset: () => void;
  handleOpenDownloadRunsModal: (
    runCount: number,
    selectedAll: boolean,
    selectedIds: string[]
  ) => void;
  handleOpenDeleteModal: (onDelete: () => void, status: boolean) => void;
  handleOpenReplayModal: (
    onReplay: () => void,
    status: boolean,
    allowTaskReplay?: boolean
  ) => void;
  hideStatusFilter?: boolean;
  isEmbed?: boolean;
  isUseRunsLoading: boolean;
  isUseRunsRefetching: boolean;
  lastDataFetch: Date | undefined;
  navigateParentWindow: (filters: string) => void;
  onDelete: (runIds: string[], isAllSelected: boolean) => void;
  onReplay: (runIds: string[], isAllSelected: boolean) => void;
  onRunsRefetch: () => void;
  rightComponent?: React.ReactNode;
  runCount: number;
  runs: Array<ZapRun>;
  showOwner?: boolean;
  showZapDetails: boolean;
  useRunsError: any;
};

const actionsStyle = css`
  grid-area: actions;
`;

const filtersStyle = getResponsiveStyles({
  desktop: css`
    grid-auto-flow: column;
  `,
  mobile: css`
    display: grid;
    grid-gap: 10px;
  `,
});

const infoStyle = getResponsiveStyles({
  desktop: css`
    align-items: flex-start;
    grid-template-areas: "select actions replay";
    grid-template-columns: 100px 1fr auto;
    grid-template-rows: 1fr;
  `,
  mobile: css`
    align-items: center;
    display: grid;
    grid-gap: 10px;
    grid-template-areas: "select replay" "actions actions";
    grid-template-columns: 100px 1fr;
    grid-template-rows: 40px 1fr;
  `,
});

const infoWithReplayStyle = getResponsiveStyles({
  desktop: css`
    grid-template-areas: "select actions replay";
    grid-template-columns: 100px 1fr auto;
    grid-template-rows: 1fr;
  `,
  mobile: css`
    grid-template-areas: "select replay" "actions actions";
    grid-template-columns: 100px 1fr;
    grid-template-rows: 40px 1fr;
  `,
});

const replayStyle = css`
  align-items: center;
  display: flex;
  height: 40px;
`;

const rightComponentStyle = css`
  grid-area: replay;
  justify-self: flex-end;
`;

const rootStyle = css`
  display: grid;
  grid-gap: 20px;
  position: relative;
`;

const selectStyle = css`
  grid-area: select;
`;

export function RunLog({
  actionableRunCount,
  filters,
  getSelectedRuns,
  handleFilterChange,
  handleFilterReset,
  handleOpenDeleteModal,
  handleOpenReplayModal,
  handleOpenDownloadRunsModal,
  hideStatusFilter = false,
  isEmbed = false,
  isUseRunsLoading,
  isUseRunsRefetching,
  lastDataFetch,
  navigateParentWindow,
  onDelete,
  onReplay,
  onRunsRefetch,
  rightComponent,
  runCount,
  runs,
  showOwner,
  showZapDetails,
  useRunsError,
}: Props) {
  const { asPath, query, pathname } = useRouter();
  const { allowTaskReplay, isTeam } = useAccountAndUser();
  const hasHydrated = useHasHydrated();
  const size = useSize({ isEmbed });
  useIFrameResize();

  const isLoading = isUseRunsLoading || !hasHydrated;

  // Allow showOwner prop to override isTeam
  const shouldShowOwner = showOwner !== undefined ? showOwner : Boolean(isTeam);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedAll, setSelectedAll] = useState(query.selected === "all");
  const outOfRetentionPeriod =
    new Date(query.end_datetime as string) <
    new Date(new Date().setDate(new Date().getDate() - 60));

  function clearSelection() {
    setSelectedIds([]);
    setSelectedAll(query.selected === "all");
  }

  useEffect(() => {
    // Refetch data when timestamp updates
    onRunsRefetch();
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastDataFetch]);

  const activeFilters = getActiveFilterNames(filters);
  const isFetchingResults = isUseRunsLoading || isUseRunsRefetching;

  // Track the time to show request results from the server
  useClock("zap-runs-actual-search-time", isFetchingResults, {
    activeFilters,
  });

  // Track the time to show results using either the cache or server
  useClock("zap-runs-time-to-results", isUseRunsLoading, {
    activeFilters,
  });

  // Track the time from navigation until the first results are shown
  useTimeSinceNavigation("zap-runs-time-to-first-results", !isUseRunsLoading, {
    activeFilters,
  });

  // We don't want to clear out the user's selection when the page changes
  const { page, ...nonPageFilters } = filters;
  useDeepEqualEffect(() => {
    // Do not persist selection if filters have changed
    clearSelection();
  }, [nonPageFilters]);

  function handlePage(pageNumber: number) {
    handleFilterChange("page", pageNumber);
  }

  function handleRowSelection(id: string, isSelected: boolean) {
    if (selectedAll) {
      if (query.selected) {
        const url = addTruthyParamsToUrl(pathname, {
          ...query,
          selected: undefined,
        });
        // If there is a selected param we need to update the URL
        // to clear it since it is changing
        navigateParentWindow(url);
      }
      setSelectedAll(false);
      setSelectedIds([]);
    } else if (isSelected) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    }
  }

  function handleSelectMenuChange(value: SelectRunOptions) {
    if (query.selected) {
      const url = addTruthyParamsToUrl(pathname, {
        ...query,
        selected: undefined,
      });

      // If there is a selected param we need to update the URL
      // to clear it since it is changing
      navigateParentWindow(url);
    }
    if (value === "all") {
      setSelectedAll(true);
      setSelectedIds(runs.map((run) => run.id));
    } else if (value === "shown") {
      // Use set to ensure that we only add each run once
      setSelectedIds([
        ...new Set([...selectedIds, ...runs.map((run) => run.id)]),
      ]);
      setSelectedAll(false);
    } else if (value === "none") {
      setSelectedAll(false);
      setSelectedIds([]);
    }
  }

  function handleDelete() {
    onDelete(selectedIds, selectedAll);
    clearSelection();
    handleOpenDeleteModal(handleDelete, false);
  }

  function handleReplay() {
    onReplay(selectedIds, selectedAll);
    clearSelection();
    handleOpenReplayModal(handleReplay, false, allowTaskReplay);
  }

  const filtersAreEmpty = areFiltersEmpty(filters);

  return (
    <div css={rootStyle}>
      {isUseRunsRefetching && <Reloader />}
      <div css={[infoStyle, infoWithReplayStyle]}>
        <span css={selectStyle}>
          <ListSelectionDropdown
            onSelect={handleSelectMenuChange}
            value={
              selectedAll ? "all" : selectedIds.length > 0 ? "shown" : "none"
            }
            passSelection={selectedIds.length > 0}
          />
        </span>
        <span css={actionsStyle}>
          {selectedIds.length > 0 || (selectedAll && runCount > 0) ? (
            <RunActions
              count={selectedAll ? actionableRunCount : selectedIds.length}
              getCachedRuns={() => getSelectedRuns(selectedIds)}
              isDisabled={false}
              onDelete={() => {
                handleOpenDeleteModal(handleDelete, true);
              }}
              onDownload={() => {
                handleOpenDownloadRunsModal(
                  actionableRunCount,
                  selectedAll,
                  selectedIds
                );
              }}
              onReplay={() => {
                handleOpenReplayModal(handleReplay, true, allowTaskReplay);
              }}
              size={size}
            />
          ) : (
            <div css={filtersStyle}>
              {hideStatusFilter ? (
                <div />
              ) : (
                <StatusFilter
                  onChange={(value) => handleFilterChange("status", value)}
                  size={size}
                  value={filters.status || []}
                />
              )}
              {isEmbed && (
                <EmbedDateFilter
                  id="EmbedDateFilter"
                  onChange={(value) => handleFilterChange("dateFilter", value)}
                  size={size === "large" ? "medium" : size}
                  value={filters.dateFilter}
                />
              )}
            </div>
          )}
        </span>
        <span css={rightComponentStyle}>
          {rightComponent || (
            <div css={replayStyle}>
              <AutoReplaySwitch />
            </div>
          )}
        </span>
      </div>
      <RunList
        isLoading={isLoading}
        onPageChange={handlePage}
        onSelect={handleRowSelection}
        page={filters.page || 1}
        selectedAll={selectedAll}
        selectedIds={selectedIds}
        showOwner={shouldShowOwner}
        showZapDetails={showZapDetails}
        size={size}
        runCount={runCount}
        runs={runs}
        url={asPath}
        isEmbed={isEmbed}
      />
      {!isLoading && runCount === 0 && (
        <NoRunsSurface
          handleFilterReset={handleFilterReset}
          hasError={Boolean(useRunsError)}
          isFilterEmpty={filtersAreEmpty}
          isOutOfRetentionPeriod={outOfRetentionPeriod}
          size={size}
        />
      )}
    </div>
  );
}
